/*
  Sticky price + Add-to-cart bar for material / configurator pages.

  A floating bar that doubles as a section navigator: it lists every
  configurator section (slab / corner / hook) as tabs, highlights the one
  currently on screen, and lets the customer jump to any section by clicking
  its tab. Price + a live Add-to-cart button for the active section stay
  pinned on the right (Billy 2026-07-01 item 9).

  - One bar, built in JS, appended to <body> once (guarded singleton).
  - Active section tracking is position-based (last section whose top passed
    a switch line), so the bar never blanks between sections.
  - The active tab auto-centres in a horizontally-scrollable nav.
  - Price mirrors the active section's price node; the button mirrors the real
    ATC button's live text ("Preparing… 5s" → "Adding…" → "Added!") and proxies
    a real click, so Turnstile / puck validation / the whole flow still run.
  - Reserves <body> padding-bottom so the floating bar never covers content.
*/
'use strict';
(function () {
  if (window.__stickyAtcBarInit) return;
  window.__stickyAtcBarInit = true;

  var SWITCH_LINE = 1 / 3;  // section flips once its TITLE reaches the top 1/3
  var SCROLL_OFFSET = 90;   // keep a jumped-to section clear of a revealed header

  var SPECS = {
    'SLAB-CONFIGURATOR':       { price: '.cfg-price', atc: '.cfg-atc' },
    'CORNER-CONFIGURATOR':     { price: '.cnr-price', atc: '.cnr-atc' },
    'HOOK-BOARD-CONFIGURATOR': { price: '.hbd-price', atc: '.hbd-atc' },
  };
  var WRAPPERS = '.product__extras-block, .product-info__configurator, .product-info__corner-configurator, .product-info__hook-board-configurator';

  // The section anchor (#cfg-slab / #cfg-closet / #cfg-corner / #cfg-hook) that a
  // configurator element lives under — used to pin the active tab on a deep link.
  function sectionAnchorFor(el) {
    var node = el;
    while (node) {
      if (node.id && /^cfg-(slab|closet|corner|hook)$/.test(node.id)) return node.id;
      node = node.parentElement;
    }
    return '';
  }

  function init() {
    var nodes = document.querySelectorAll('slab-configurator, corner-configurator, hook-board-configurator');
    var units = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var spec = SPECS[el.tagName];
      if (!spec) continue;
      var priceEl = el.querySelector(spec.price);
      var atcEl = el.querySelector(spec.atc);
      if (!priceEl || !atcEl) continue;
      units.push({
        el: el, priceEl: priceEl, atcEl: atcEl,
        label: el.getAttribute('data-sticky-label') || 'Section',
        scrollTarget: el.closest(WRAPPERS) || el,
        anchor: sectionAnchorFor(el),
        tab: null,
      });
    }
    if (!units.length) return;

    var bar = document.createElement('div');
    bar.className = 'sticky-atc-bar';
    bar.setAttribute('hidden', '');

    var inner = document.createElement('div');
    inner.className = 'sticky-atc-bar__inner';

    var nav = document.createElement('nav');
    nav.className = 'sticky-atc-bar__nav';
    units.forEach(function (u) {
      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'sticky-atc-bar__tab';
      tab.textContent = u.label;
      tab.addEventListener('click', function () {
        var y = u.scrollTarget.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      });
      u.tab = tab;
      nav.appendChild(tab);
    });

    var right = document.createElement('div');
    right.className = 'sticky-atc-bar__right';
    var priceOut = document.createElement('span');
    priceOut.className = 'sticky-atc-bar__price';
    priceOut.textContent = '—';
    var btnEl = document.createElement('button');
    btnEl.type = 'button';
    btnEl.className = 'sticky-atc-bar__btn';
    btnEl.setAttribute('aria-label', 'Add to cart');
    btnEl.innerHTML =
      '<svg class="sticky-atc-bar__btn-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle>' +
        '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>' +
      '</svg>' +
      '<span class="sticky-atc-bar__btn-text">Add to cart</span>';
    var btnText = btnEl.querySelector('.sticky-atc-bar__btn-text');
    right.appendChild(priceOut);
    right.appendChild(btnEl);

    inner.appendChild(nav);
    inner.appendChild(right);
    bar.appendChild(inner);
    document.body.appendChild(bar);

    var active = null;
    var mo = new MutationObserver(function () { syncFace(); });

    // Deep-link pin: when the page opens with a #cfg-<section> hash (a category
    // collection deep link), lock the active tab to that section until the
    // customer actually scrolls. Otherwise the position-based tracker briefly
    // lights up a neighbouring tab during the programmatic scroll-into-view
    // (Billy 2026-07-21: "sticky bar highlights neighbouring section until scroll").
    var pinnedUnit = null;
    (function () {
      var h = (window.location.hash || '').replace('#', '');
      if (!h) return;
      for (var i = 0; i < units.length; i++) {
        if (units[i].anchor && units[i].anchor === h) { pinnedUnit = units[i]; break; }
      }
    })();
    // A genuine user scroll gesture (not the programmatic deep-link animation,
    // which fires 'scroll' but never wheel/touch/key) releases the pin.
    function clearPin() { pinnedUnit = null; }
    ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, clearPin, { passive: true, once: true });
    });

    function syncFace() {
      if (!active) return;
      priceOut.textContent = active.priceEl.textContent;
      btnText.textContent = (active.atcEl.textContent || '').trim() || 'Add to cart';
      btnEl.disabled = !!active.atcEl.disabled;
    }

    // Scroll the nav only enough to bring the active tab fully into view (with
    // a little padding) — never centre it, so activating one tab doesn't push
    // its neighbour half off the edge.
    function ensureTabVisible(tab) {
      var tabRect = tab.getBoundingClientRect();
      var navRect = nav.getBoundingClientRect();
      var pad = 14;
      if (tabRect.left < navRect.left + pad) {
        nav.scrollLeft -= (navRect.left + pad) - tabRect.left;
      } else if (tabRect.right > navRect.right - pad) {
        nav.scrollLeft += tabRect.right - (navRect.right - pad);
      }
    }

    function setActive(u) {
      if (active !== u) {
        if (active && active.tab) active.tab.classList.remove('is-active');
        active = u;
        mo.disconnect();
        if (u) {
          if (u.tab) { u.tab.classList.add('is-active'); ensureTabVisible(u.tab); }
          mo.observe(u.priceEl, { childList: true, characterData: true, subtree: true });
          mo.observe(u.atcEl, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
        }
      }
      syncFace();
    }

    function showBar() {
      bar.removeAttribute('hidden');
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var top = bar.getBoundingClientRect().top;
      document.body.style.paddingBottom = Math.max(0, vh - top) + 'px';
    }
    function hideBar() {
      bar.setAttribute('hidden', '');
      document.body.style.paddingBottom = '';
    }

    function update() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      // Track off each section's WRAPPER (which starts at the section title),
      // not the configurator element (the form), so the tab switches when the
      // title enters the top third of the screen.
      var firstTop = units[0].scrollTarget.getBoundingClientRect().top;
      var lastBottom = units[units.length - 1].scrollTarget.getBoundingClientRect().bottom;

      if (firstTop > vh || lastBottom < 0) { hideBar(); return; }

      var refLine = vh * SWITCH_LINE;
      var chosen = units[0];
      for (var i = 0; i < units.length; i++) {
        if (units[i].scrollTarget.getBoundingClientRect().top <= refLine) chosen = units[i];
      }
      // While a deep-link pin is active, keep the target section's tab lit
      // regardless of the in-flight scroll position.
      if (pinnedUnit) chosen = pinnedUnit;
      setActive(chosen);
      showBar();
    }

    btnEl.addEventListener('click', function () {
      if (active && active.atcEl && !active.atcEl.disabled) active.atcEl.click();
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    setInterval(update, 1200);
    update();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
